import { Box, Divider, FormHelperText, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { OptionType, SGSCheckbox, SGSForm } from 'components';
import { useAppContext } from 'core/contexts';
import { Perimetre } from 'core/models';
import { compare } from 'core/utils/utils';
import { useUtilisateurFromContext } from 'features/utilisateurs/contexts/UtilisateurFormProvider';
import { hasRiskRight } from 'features/utilisateurs/utils/utils';
import { delay } from 'lodash';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useUserPerimetreContext } from '../../contexts/UtilisateurPerimetreProvider';
import { ListWithSearch } from '../ListBoxWithSearch';
import styles from '../Utilisateurs.module.scss';

type Props = {
    setValue: Function;
    errors: any;
    trigger?: Function;
    displayRisk?: boolean;
    readOnly?: boolean;
    displayExcludeVIP?: boolean;
};

export const FormPerimetre: React.FC<PropsWithChildren<Props>> = ({ setValue, errors, trigger, displayRisk = true, readOnly = false, displayExcludeVIP = true }) => {
    const { user } = useAppContext();
    const location = useLocation();
    const {
        perimetre,
        sante,
        setSante,
        prevoyance,
        setPrevoyance,
        colleges,
        setColleges,
        selectedColleges,
        setSelectedColleges,
        souscrips,
        setSouscrips,
        selectedSouscrips,
        setSelectedSouscrips,
        isLoadingSouscrips,
        setIsLoadingSouscrips,
        isLoadingColleges,
        setIsLoadingColleges,
        isLoadingPerimetre,
        hasPerimetreErreur,
        isUpdatingData,
        setIsUpdatingData,
        excludeVIP,
        setExcludeVIP,
    } = useUserPerimetreContext();

    const { selectedUser, userConnected } = useUtilisateurFromContext();

    const isAdminAndOnPageProfil = () => {
        // user detail
        if (selectedUser.id === user.id && selectedUser.roles.includes('ROLE_ADMIN')) return true;
        // page profil
        if (user.roles.includes('ROLE_ADMIN') && location.pathname === '/utilisateurs/modifier') return true;
        return false;
    };

    const handleSetSante = (value) => {
        if (readOnly) return;
        setSante(value);
        setValue('perimetre.risques.sante', value);
        trigger('perimetre.risques');
    };

    const handleSetPrev = (value) => {
        if (readOnly) return;
        setPrevoyance(value);
        setValue('perimetre.risques.prevoyance', value);
        trigger('perimetre.risques');
    };

    const handleSouscripSelect = (selectedSouscrips: OptionType) => {
        if (readOnly) return;
        setSelectedSouscrips(selectedSouscrips);
        setValue('perimetre.souscripteurs', selectedSouscrips, { shouldDirty: true });
        trigger('perimetre.souscripteurs');
    };

    const handleCollegeSelect = (selectedColleges: OptionType) => {
        if (readOnly) return;
        setSelectedColleges(selectedColleges);
        setValue('perimetre.colleges', selectedColleges, { shouldDirty: true });
        trigger('perimetre.colleges');
    };

    const handleSetExcludeVIP = (excludeVIP: boolean) => {
        if (readOnly) return;
        setExcludeVIP(excludeVIP);
        setValue('perimetre.excludeVIP', excludeVIP);
        trigger('perimetre.excludeVIP');
    };

    const risks = useCallback(() => {
        let risks = [];
        if (user.id === selectedUser.id && selectedUser.role === 'ADMIN') {
            risks = ['S', 'P'];
        }
        if (sante) risks.push('S');
        if (prevoyance) risks.push('P');
        return risks;
    }, [sante, prevoyance]);

    const filterSouscrips = useCallback(
        (perimetre: Perimetre[]) => {
            if (!perimetre) return;
            let souscripOpts = [];
            const risk = risks();
            const ids = [];
            let sortedData = [];
            if (risk.length) {
                perimetre.forEach((item) => {
                    // Constraints
                    const isFiltered = risk.length === 0 ? false : true;
                    // Get data
                    if ((risk.includes(item.typ) || !isFiltered) && !ids.includes(item.codSouscrip)) {
                        ids.push(item.codSouscrip);
                        souscripOpts.push({ value: item.codSouscrip, label: item.souscrip });
                    }
                });
                sortedData = souscripOpts.sort(compare);
            }
            setSouscrips(sortedData);
            setIsLoadingSouscrips(false);
        },
        [setSouscrips, risks, setIsLoadingSouscrips],
    );

    const filterColleges = useCallback(
        (perimetre: Perimetre[]) => {
            if (!perimetre) return;
            let collegeOpts = [];
            const risk = risks();
            const ids = [];
            let sortedData = [];
            if (selectedSouscrips.length) {
                const souscriptIds = selectedSouscrips ? selectedSouscrips.map((opt) => opt.value) : [];
                perimetre.forEach((item) => {
                    // Constraints
                    const isFiltered = risk.length === 0 ? false : true;
                    const result = [true];
                    result.push(risk.includes(item.typ));
                    result.push(souscriptIds.includes(item.codSouscrip));
                    // Get data
                    if ((!result.includes(false) || !isFiltered) && !ids.includes(item.codcol)) {
                        ids.push(item.codcol);
                        collegeOpts.push({ value: item.codcol, label: item.college });
                    }
                });
                sortedData = collegeOpts.sort(compare);
            }
            setColleges(sortedData);
            setIsLoadingColleges(false);
            setIsUpdatingData(false);
        },
        [setColleges, selectedSouscrips, risks, setIsLoadingColleges, setIsUpdatingData],
    );

    const setSouscripsOptions = useCallback(() => {
        if (isAdminAndOnPageProfil()) {
            setSelectedSouscrips(souscrips);
        } else {
            const risk = risks();
            if (!isLoadingSouscrips && !souscrips.length) {
                if (risk.length > 0) return;
            }
            const souscriptIds = selectedSouscrips ? selectedSouscrips.map((opt) => opt.value) : [];
            const newSelectedSouscrips = souscrips.filter((opt) => souscriptIds.includes(opt.value));
            setSelectedSouscrips(newSelectedSouscrips);
        }
    }, [souscrips, isLoadingSouscrips, risks, selectedSouscrips, setSelectedSouscrips]);

    const setCollegesOptions = useCallback(() => {
        if (isAdminAndOnPageProfil()) {
            setSelectedColleges(colleges);
        } else {
            const risk = risks();
            if (!isLoadingSouscrips && !souscrips.length) {
                if (risk.length > 0) return;
            }
            const collegeIds = selectedColleges ? selectedColleges.map((opt) => opt.value) : [];
            const newSelectedColleges = colleges.filter((opt) => collegeIds.includes(opt.value));
            setSelectedColleges(newSelectedColleges);
        }
    }, [colleges, isLoadingSouscrips, risks, selectedColleges, setSelectedColleges, souscrips.length]);

    const VIP = () => {
        setExcludeVIP(() => {
            if (!displayExcludeVIP) return true;
            return user.id === selectedUser.id && selectedUser.role === 'ADMIN' ? false : selectedUser.perimetre.excludeVIP;
        });
    };

    useEffect(() => {
        setIsLoadingSouscrips(true);
        filterSouscrips(perimetre);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        filterSouscrips(perimetre);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser]);

    // Filter souscrips
    useEffect(() => {
        if (!isLoadingSouscrips || isUpdatingData) {
            setIsUpdatingData(true);
            //filterSouscrips(perimetre);
            delay(filterSouscrips, 5, perimetre);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sante, prevoyance, perimetre]);

    // Filter colleges
    useEffect(() => {
        if (!isLoadingPerimetre || isUpdatingData) {
            setIsLoadingColleges(true);
            delay(filterColleges, 5, perimetre);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSouscrips]);

    // Manage souscrips selected option
    useEffect(() => {
        setSouscripsOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [souscrips]);

    // Manage colleges selected option
    useEffect(() => {
        setCollegesOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colleges]);

    useEffect(() => {
        VIP();
    }, [selectedUser]);

    const hasContract = (type: string) => {
        return perimetre.some((item) => item.typ === type);
    };

    const isSanteChecked = (): boolean => {
        if (isAdminAndOnPageProfil()) return true;
        return hasRiskRight(userConnected, 'S') ? sante : false;
    };

    const isPrevChecked = (): boolean => {
        if (isAdminAndOnPageProfil()) return true;
        return hasRiskRight(userConnected, 'P') ? prevoyance : false;
    };

    const isExcludeVIPChecked = (): boolean => {
        if (isAdminAndOnPageProfil()) return false;
        return excludeVIP;
    };

    return (
        <>
            {isLoadingPerimetre ? (
                <Box>Chargement...</Box>
            ) : (
                <>
                    {displayRisk && (
                        <>
                            <Typography className={classNames(styles.SGStitle, errors.perimetre?.risques ? styles.msgError : '')}>Risque(s) :</Typography>
                            <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
                            <SGSForm.Row>
                                <SGSForm.Col md={2}>
                                    <SGSCheckbox
                                        label={'Santé'}
                                        name="perimetre.risques.sante"
                                        checked={isSanteChecked()}
                                        handleChange={handleSetSante}
                                        disabled={hasRiskRight(userConnected, 'S') && hasContract('S') ? false : true}
                                        triggerByName={{ trigger, name: 'risques' }}
                                        readOnly={readOnly}
                                    />
                                </SGSForm.Col>
                                <SGSForm.Col md={2}>
                                    <SGSCheckbox
                                        label={'Prévoyance'}
                                        name="perimetre.risques.prevoyance"
                                        checked={isPrevChecked()}
                                        handleChange={handleSetPrev}
                                        disabled={hasRiskRight(userConnected, 'P') && hasContract('P') ? false : true}
                                        triggerByName={{ trigger, name: 'risques' }}
                                        readOnly={readOnly}
                                    />
                                </SGSForm.Col>
                            </SGSForm.Row>
                        </>
                    )}
                    <SGSForm.Row>
                        <SGSForm.Col md={12}>
                            <Box className="h-100">
                                {errors?.perimetre?.risques && (
                                    <FormHelperText className=" ml-4 mt-n4 " error>
                                        {(errors.perimetre.risques as any).message}
                                    </FormHelperText>
                                )}
                            </Box>
                        </SGSForm.Col>
                    </SGSForm.Row>

                    <Typography className={classNames(styles.SGStitle, 'mt-0', errors.perimetre?.souscripteurs ? styles.msgError : '')}>Souscripteur(s):</Typography>
                    <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
                    <ListWithSearch
                        name={'souscripteurs'}
                        list={souscrips || []}
                        selected={selectedSouscrips || []}
                        setSelected={handleSouscripSelect}
                        isLoading={isLoadingSouscrips || isLoadingPerimetre || isUpdatingData}
                        noOptionLabel={'Veuillez sélectionner un risque'}
                        hasError={hasPerimetreErreur}
                        displayTools={!readOnly}
                    ></ListWithSearch>
                    <Box className={styles.formErrorMsg}>
                        {errors?.perimetre?.souscripteurs && (
                            <FormHelperText className="ml-4" error>
                                {(errors?.perimetre?.souscripteurs as any).message}
                            </FormHelperText>
                        )}
                    </Box>

                    <Typography className={classNames(styles.SGStitle, 'mt-5', errors.perimetre?.colleges ? styles.msgError : '')}>Collège(s):</Typography>
                    <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
                    <ListWithSearch
                        name={'colleges'}
                        list={colleges || []}
                        selected={selectedColleges || []}
                        setSelected={handleCollegeSelect}
                        isLoading={isLoadingColleges || isLoadingPerimetre || isUpdatingData}
                        noOptionLabel={'Veuillez sélectionner un souscripteur'}
                        hasError={hasPerimetreErreur}
                        displayTools={!readOnly}
                    ></ListWithSearch>
                    <Box className={styles.formErrorMsg}>
                        {errors.perimetre?.colleges && (
                            <FormHelperText className="ml-4" error>
                                {(errors.perimetre?.colleges as any).message}
                            </FormHelperText>
                        )}
                    </Box>

                    {displayExcludeVIP && (
                        <>
                            <Typography className={classNames(styles.SGStitle, 'mt-5', errors.perimetre?.colleges ? styles.msgError : '')}>VIP:</Typography>
                            <Divider className={classNames(styles.SGSDivider, 'mb-4')} />
                            <SGSForm.Row>
                                <SGSForm.Col>
                                    <SGSCheckbox
                                        label={'Interdire la visualisation des salariés VIP'}
                                        name="perimetre.excludeVIP"
                                        checked={isExcludeVIPChecked()}
                                        handleChange={handleSetExcludeVIP}
                                        triggerByName={{ trigger, name: 'excludeVIP' }}
                                        readOnly={readOnly}
                                    />
                                </SGSForm.Col>
                            </SGSForm.Row>
                        </>
                    )}
                </>
            )}
        </>
    );
};
